import {
  getCountryByCountryCode,
  getLocationPlans,
  LocalProjectAddon,
  Location,
  projectLocationAddonMapper,
  Registration,
} from "@ecologi/shared";
import {
  ConfirmCardPaymentData,
  ConfirmCardSetupData,
} from "@stripe/stripe-js";
import { APIError, confirmPaymentIntent } from "../../../lib";
import { createSubscription } from "../../../lib/api/subscriptions";
import {
  selectPaymentErrorMessage,
  selectSubscriptionAddons,
} from "../selectors";
import { CreateSubscriptionHandler } from "./use-create-subscription";

const getPlanType = (
  planShortCode: string,
  location: Location
): {
  isBusinessPlan: boolean;
  isFamilyPlan: boolean;
} => {
  const { businessPlanIdForLocation, familyPlanIdForLocation } =
    getLocationPlans(location);

  return {
    isFamilyPlan: planShortCode === familyPlanIdForLocation,
    isBusinessPlan: planShortCode === businessPlanIdForLocation,
  };
};

const getLocalProject = (location: Location): LocalProjectAddon | null =>
  location ? projectLocationAddonMapper[location.countryCode] : null;

export const createSubscriptionHandler: CreateSubscriptionHandler = async ({
  stripe,
  location,
  referrerUserId,
  affiliateClickId,
  parentUsername,
  ...data
}) => {
  const {
    cardElement,
    formValues,
    selectedPlanAddons,
    selectedPlanId: planShortCode,
    numLocalTrees,
    captchaToken,
  } = data;

  const type = getPlanType(planShortCode, location);
  const info = getLocationPlans(location);

  // 1. Create a Customer, User & Subscription, return the clientSecret for
  //    the subscription PaymentIntent which will be confirmed using the
  //    PaymentMethod gathered in the cardElement
  const { user, clientSecret, paymentAmount, currency } =
    await createSubscription({
      type: type.isBusinessPlan ? "business" : "individual",
      captchaToken,
      customer: {
        emailAddress: data.formValues.emailAddress,
        lastName: data.formValues.lastName,
        firstName: data.formValues.firstName,
        postCode: data.formValues.postCode,
        country: getCountryByCountryCode(data.formValues.country),
        location: data.formValues.country,
      },
      user: {
        password: data.formValues.password,
        groupName: data.formValues.groupName || null,
        referralSource: data.formValues.referralSource as any,
        isMarketingAccepted: data.formValues.isMarketingAccepted,
        referrerUserId,
        affiliateClickId,
        parent: parentUsername,
      },
      subscription: {
        shortCode: planShortCode,
        addons: selectSubscriptionAddons(
          planShortCode,
          type.isFamilyPlan
            ? info.familyAddonsForLocation
            : info.businessAddonsForLocation,
          getLocalProject(location),
          selectedPlanAddons,
          numLocalTrees
        ).map((a) => ({ quantity: a.quantity, shortCode: a.id })),
      },
      testClockId: data.formValues.testClockId,
    });

  const createSubscriptionResponse = {
    user,
    currency,
    paymentAmount,
    selectedPlanId: planShortCode,
    referralSource: formValues.referralSource,
    type: type.isFamilyPlan
      ? "family"
      : type.isBusinessPlan
      ? "business"
      : "individual",
  };

  const intentType = Registration.inferIntentTypeFromClientSecret(clientSecret);

  if (intentType === Registration.IntentType.None) {
    user.subscription.isPaused = false;

    return createSubscriptionResponse;
  }

  const paymentData: ConfirmCardPaymentData | ConfirmCardSetupData = {
    payment_method: {
      card: cardElement,
      billing_details: { name: `${user.firstName} ${user.lastName}` },
    },
  };

  try {
    if (intentType === Registration.IntentType.Setup) {
      await stripe.confirmCardSetup(clientSecret, paymentData);

      user.subscription.isPaused = false;

      return createSubscriptionResponse;
    }
    // 2. Confirm the card payment using the cardElement details,
    //    this creates a PaymentMethod on Stripe as a side effect
    const { paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      paymentData
    );

    // 2.1 Handle 3DS-secure events where payment requires additional action
    if (paymentIntent?.status === "requires_action") {
      const cardConfirmationRes = await stripe.handleCardAction(
        paymentIntent.client_secret
      );

      if (cardConfirmationRes.error) {
        throw cardConfirmationRes.error;
      }

      // Confirm the payment intent with Stripe (from the server)
      await confirmPaymentIntent(
        paymentIntent.id,
        paymentIntent.payment_method as string
      );
    }
  } catch (error) {
    // Allow 'card_declined' errors because of difficulty cleaning up
    // impact is mocked until receipt of `invoice.paid` or `invoice.payment_failed`
    // We will prompt user to re-enter their card once signed up
    if (error.code !== "card_declined") {
      const { code, raw } = error.errors?.[0] || {
        code: error.code,
        raw: error.message,
      };

      const errorMessage =
        code && raw
          ? selectPaymentErrorMessage(code, raw.message)
          : typeof (raw ?? error) === "string"
          ? raw ?? error
          : "An unknown error occurred. Please try again or contact us at support@ecologi.com if the issue persists";

      throw new APIError(400, {
        error: errorMessage,
      });
    }
  }

  user.subscription.isPaused = false;

  return createSubscriptionResponse;
};
